import { Checkbox, Loading } from "@components";
import styles from "@styles/home-configurations.module.scss";
import classNames from "classnames/bind";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { useEffect, useState } from "react";
import { ForEach, If } from "shared/components/control-statements";
import { InputSearch } from "shared/components/InputSearch";
import Toggle2 from "shared/components/Toggle2";
import { UserStorage } from "shared/tools/UserStorage";
const css = classNames.bind(styles);

const DeliveryType = {
  DELIVER_ON_ADDRESS: 1,
  GET_ON_PLACE: 2,
  INDOOR: 3,
};

export default function BrandUnits({ data, onChange }) {
  const [allBrands, setAllBrands] = useState([]);
  const [brands, setBrands] = useState(data?.brands || []);
  const [units, setUnits] = useState(data?.units || []);
  const [channels, setChannels] = useState(data?.channels || []);
  const [collapsedByBrand, setCollapsedByBrand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    onChange({ units, brands, channels });
  }, [units, brands, channels]);

  const loadBrandAndUnits = async () => {
    let { environment_id, user_type } = UserStorage.getLoggedUser();

    if (user_type === "BRAND") {
      const holdingResponse = await EnvironmentsAPI.getById(environment_id);
      environment_id = holdingResponse.getData({}).environment_id;
    }

    const response = await EnvironmentsAPI.getUnits(String(environment_id));
    const brands = response.getData({}).brands;

    const brandsList = [];

    for (const b of brands) {
      const response = await EnvironmentsAPI.getUnits(String(b.id));
      const units = response.getData({})["units"];

      brandsList.push({
        id: b.id,
        name: b.name,
        units,
      });
    }

    setAllBrands(brandsList);
    setUnits(
      data?.units ||
        brandsList.flatMap((b) =>
          b.units.filter((u) => u.active).map((u) => u.id),
        ),
    );
    setBrands(data?.brands || brandsList.flatMap((b) => b.id));
    setChannels(
      data?.channels || [
        DeliveryType.DELIVER_ON_ADDRESS,
        DeliveryType.GET_ON_PLACE,
        DeliveryType.INDOOR,
      ],
    );
    setLoading(false);
  };

  useEffect(() => {
    loadBrandAndUnits();
  }, []);

  const Brand = ({
    brand,
    selectedUnits,
    onToggleUnit,
    onToggleAll,
    collapsed,
    onToggleCollapsed,
    search,
  }) => {
    const isSomeChecked = brand.units.reduce(
      (r, u) => r || selectedUnits.includes(u.id),
      false,
    );

    const activeUnits = brand.units.filter((u) => u.active);

    const filteredUnits = Object.values(activeUnits).filter((c) => {
      return search
        ? c.name?.toLowerCase().includes(search.toLowerCase())
        : true;
    });

    if (activeUnits.length === 0) return;

    return (
      <div className={styles.brand}>
        <div
          className={css({ header: true, collapsed })}
          onClick={() => onToggleCollapsed(brand.id)}
        >
          <div className="d-flex align-center justify-between w-100">
            <div className="d-flex align-center gap-10">
              <Toggle2
                checked={isSomeChecked}
                onChange={() => {
                  onToggleAll(brand.id, !isSomeChecked);
                }}
              />
              <div className={styles.divisor} />
              <p className={styles.brandName}>{brand?.name}</p>
            </div>
            <div className="d-flex align-center gap-20">
              <p className={styles.qty}>
                <strong>
                  {
                    (
                      filteredUnits.filter((u) => {
                        return selectedUnits.includes(u.id);
                      }) || []
                    ).length
                  }
                </strong>
                /<span>{filteredUnits?.length}</span>
              </p>
              <i className="fas fa-chevron-down" />
            </div>
          </div>
        </div>

        {collapsed && (
          <div className={styles.content}>
            <div className={styles.optionUnits}>
              <ForEach
                items={filteredUnits}
                render={(u) => (
                  <div
                    className={styles.option}
                    onClick={() => onToggleUnit(brand.id, u.id)}
                  >
                    <Checkbox
                      label={u.name}
                      design="two"
                      checked={selectedUnits.indexOf(u.id) > -1}
                      readOnly
                    />
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const multibrands = allBrands.length > 1;

  return (
    <>
      <If cond={loading}>
        <div className={styles.brandsUnitsContainer}>
          <div className="d-flex justify-center">
            <Loading />
          </div>
        </div>
      </If>

      <If cond={!loading}>
        <div className={styles.channelsContainer}>
          <p className={styles.title}>Quais são os modelos de operação?</p>
          <p className={styles.subtitle}>
            Determine ao menos uma modalidade de funcionamento para
            disponibilizar os produtos.
          </p>
          <div className={styles.channels}>
            <div
              className={css({
                channel: true,
                selected: channels.includes(DeliveryType.DELIVER_ON_ADDRESS),
              })}
              onClick={() =>
                setChannels((prev) =>
                  !prev.includes(DeliveryType.DELIVER_ON_ADDRESS)
                    ? [...prev, DeliveryType.DELIVER_ON_ADDRESS]
                    : prev.filter((c) => c !== DeliveryType.DELIVER_ON_ADDRESS),
                )
              }
            >
              <div className="d-flex align-center gap-10">
                <i className="fal fa-motorcycle" />
                <p>Delivery</p>
              </div>
              <Checkbox
                design="two"
                checked={channels.includes(DeliveryType.DELIVER_ON_ADDRESS)}
              />
            </div>
            <div
              className={css({
                channel: true,
                selected: channels.includes(DeliveryType.GET_ON_PLACE),
              })}
              onClick={() =>
                setChannels((prev) =>
                  !prev.includes(DeliveryType.GET_ON_PLACE)
                    ? [...prev, DeliveryType.GET_ON_PLACE]
                    : prev.filter((c) => c !== DeliveryType.GET_ON_PLACE),
                )
              }
            >
              <div className="d-flex align-center gap-10">
                <i className="fal fa-home" />
                <p>Retirar e levar</p>
              </div>
              <Checkbox
                design="two"
                checked={channels.includes(DeliveryType.GET_ON_PLACE)}
              />
            </div>
            <div
              className={css({
                channel: true,
                selected: channels.includes(DeliveryType.INDOOR),
              })}
              onClick={() =>
                setChannels((prev) =>
                  !prev.includes(DeliveryType.INDOOR)
                    ? [...prev, DeliveryType.INDOOR]
                    : prev.filter((c) => c !== DeliveryType.INDOOR),
                )
              }
            >
              <div className="d-flex align-center gap-10">
                <i className="fal fa-utensils" />
                <p>Comer no local</p>
              </div>
              <Checkbox
                design="two"
                checked={channels.includes(DeliveryType.INDOOR)}
              />
            </div>
          </div>
        </div>

        <div className="mt-15" />

        <div className={styles.brandsUnitsContainer}>
          <p className={styles.title}>
            {multibrands ? "Marcas e unidades" : "Unidades"}
          </p>
          <p className={styles.subtitle}>
            Marque/desmarque as unidades desejadas.
          </p>
          <InputSearch
            placeholder="Buscar por..."
            design="three"
            className="mt-10 mb-10"
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.brandsUnits}>
            <ForEach
              items={allBrands}
              render={(b) => {
                const isCollapsed = collapsedByBrand.includes(b.id);

                if (search) {
                  const searchLower = search.toLowerCase();
                  if (
                    !b.name.toLowerCase().includes(searchLower) &&
                    !b.units.some((u) =>
                      u.name.toLowerCase().includes(searchLower),
                    )
                  )
                    return null;
                }

                return (
                  <Brand
                    brand={b}
                    selectedUnits={units}
                    onToggleUnit={(brandId, unitId) => {
                      setUnits((prevUnits) => {
                        let newUnits;

                        if (prevUnits.includes(unitId)) {
                          newUnits = prevUnits.filter((u) => u !== unitId);

                          setBrands((prevBrands) => {
                            if (newUnits.length > 0)
                              return [...new Set([...prevBrands, brandId])];

                            return prevBrands.filter((b) => b !== brandId);
                          });
                        } else {
                          newUnits = [...prevUnits, unitId];

                          setBrands((prevBrands) => [
                            ...new Set([...prevBrands, brandId]),
                          ]);
                        }

                        return newUnits;
                      });
                    }}
                    collapsed={isCollapsed}
                    onToggleAll={(brandId, checkAll) => {
                      const unitIds = b.units.map((u) => u.id);
                      if (checkAll) {
                        setUnits([...new Set([...units, ...unitIds])]);
                        setBrands([...new Set([...brands, brandId])]);
                      } else {
                        setUnits(units.filter((u) => unitIds.indexOf(u) == -1));
                        setBrands(brands.filter((b) => b !== brandId));
                      }
                    }}
                    onToggleCollapsed={(brand) => {
                      setCollapsedByBrand((prev) =>
                        prev.includes(brand)
                          ? prev.filter((id) => id !== brand)
                          : [...prev, brand],
                      );
                    }}
                    search={search}
                  />
                );
              }}
            />
          </div>
        </div>
      </If>
    </>
  );
}
